import React, { useMemo, useState, useRef, useEffect } from "react";
import { Platform, Animated } from "react-native";
import { useNavigation, useRoute, type RouteProp } from "@react-navigation/native";
import { useAuth } from "../../../hooks/useAuth";
import { useVisitSession } from "../../../contexts/VisitSessionContext";
import { useAppState, actions } from "../../../contexts/AppStateContext";
import { useGetEnrolledCoursesForStudentQuery } from "@axio/graphql/generated/graphql";
import { useAppConfig } from "../../../AppConfigProvider";
import useWebSocket from "../../../hooks/useWebSocket";
import handleOpenLink from "../../../utils/handleOpenLink";
import { TodosIcon, EducationIcon, CareerIcon, HealthIcon } from "../../../components/svgs/menuIcons";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type { RootStackParamList } from "../../../navigation/navigation.types";
import TopMenu from "../../../components/navigation/TopMenu/TopMenu";
import { useChatBar } from "../../../contexts/ChatBarContext";
import { useFocusTrap } from "../../../hooks/useFocusTrap";

import SlideInMenuContainer from "../SlideInMenuContainer/SlideInMenuContainer";
import { useCourseDetails } from "../../../contexts/CourseDetailsContext";
import { RolePermissions } from "../../../constants/rolesAndPermissions";
import { useNotification } from "../../../contexts/NotificationContext";
import { getValidAccessToken } from "../../../utils/authRefresh";

type MenuItem = {
  display: string;
  name: keyof RootStackParamList;
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  externalURL: string | undefined;
  index: number;
  requiredPermission?: string;
};

const ITEMS_PER_PAGE = 30;

function TopMenuContainer() {
  const appConfig = useAppConfig();
  const { sendMessage } = useWebSocket();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList>>();

  const { setShowChat, handleChatWindowOpenState } = useChatBar();

  //Notifcation based on route name.

  const { logout, userInfo } = useAuth();
  const { endSession } = useVisitSession();
  const { dispatch } = useAppState();

  const schoolId = appConfig.axioSchools[0];

  const { data: courses, loading: coursesLoading } = useGetEnrolledCoursesForStudentQuery({
    variables: {
      schoolId,
      page: 1,
      pageSize: ITEMS_PER_PAGE,
    },
    skip: !schoolId,
  });

  const [progressVisible, setProgressVisible] = useState(false);
  const slideAnim = useRef(new Animated.Value(270)).current;

  const { trapRef: slideInRef } = useFocusTrap({
    visible: progressVisible,
    onEscape: () => setProgressVisible(false),
  });

  const { toggleNotifications, setNotificationsVisible } = useNotification();

  const { isVisible: isCourseDetailsVisible, setIsVisible: setIsCourseDetailsVisible } = useCourseDetails();

  const { scope, isLTI, showMiniLogo, logo, miniLogo, isCompanionMode } = appConfig;

  const companionName = appConfig.companionName;

  const DomainConfigMenuItems: MenuItem[] = [
    { display: "Dashboard", name: "Home", icon: CareerIcon, externalURL: undefined, index: 0 },
    { display: "Companion", name: "Companion Home", icon: CareerIcon, externalURL: undefined, index: 0 },
    { display: "Course Catalogue", name: "Course Catalogue", icon: EducationIcon, externalURL: undefined, index: 1 },
    { display: "Courses", name: "Custom Course Student", icon: EducationIcon, externalURL: undefined, index: 2 },
    { display: "Portfolio", name: "Portfolio", icon: CareerIcon, externalURL: undefined, index: 4 },
    { display: "Career", name: "Career", icon: CareerIcon, externalURL: undefined, index: 6 },
    { display: "Insights", name: "Insights", icon: CareerIcon, externalURL: undefined, index: 8 },
  ];

  const menuItems = useMemo(() => {
    const filtered = DomainConfigMenuItems.filter((item) => {
      let permissionCheck = true;
      if (item.requiredPermission) {
        permissionCheck = userInfo.userPermissions.includes(item.requiredPermission);
      }
      return scope.includes(item.name) && permissionCheck;
    }).sort((a, b) => a.index - b.index);

    const homeItem = DomainConfigMenuItems.find((item) =>
      isCompanionMode ? item.name === "Companion Home" : item.name === "Home"
    );
    if (
      homeItem &&
      !filtered.some((item) => (isCompanionMode ? item.name === "Companion Home" : item.name === "Home"))
    ) {
      filtered.unshift(homeItem);
    }

    return filtered;
  }, [scope, userInfo.userPermissions]);

  useEffect(() => {
    if (isCourseDetailsVisible) {
      setNotificationsVisible(false);
      setProgressVisible(false);
    }
  }, [isCourseDetailsVisible]);

  useEffect(() => {
    if (progressVisible) {
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(slideAnim, {
        toValue: 270,
        duration: 300,
        useNativeDriver: true,
      }).start();
    }
  }, [progressVisible, slideAnim]);

  const handleItemPress = async (item) => {
    if (item.name === "Coaching") {
      setShowChat(true);
      await handleNavigate();
      navigation.navigate("Custom School Student Courses");
      return;
    } else {
      handleChatWindowOpenState(false);
    }
    if (item.externalURL) {
      let newWindow;
      if (Platform.OS === "web") {
        newWindow = window.open("", "_blank");
      }
      handleOpenLink(item.externalURL, newWindow);
      return;
    } else if (item.name === "Custom Course Student") {
      // item.name === "Custom Course Student" is a string in the tenantConfig.scope, so even though it does not explain the context anymore, it is still used for the comparison logic
      // But we can change the tenant configs to have a cleaner logic (faruk)
      handleCourseworkNavigation();
    } else {
      await handleNavigate();
      navigation.navigate(item.name);
    }
  };

  const handleNotificationsToggle = () => {
    toggleNotifications();
  };

  const handleCourseworkNavigation = async () => {
    await handleNavigate();
    if (userInfo.userPermissions.includes(RolePermissions.courseEdit)) {
      navigation.navigate("Custom School Faculty Courses");
    } else {
      navigation.navigate("Custom School Student Courses");
    }
  };

  const handleProgressToggle = () => {
    setProgressVisible(!progressVisible);
  };

  const handleSettingsPress = async () => {
    await handleNavigate();
    navigation.navigate("Settings");
  };

  const handleLogoutPress = async () => {
    await endSession();
    logout();
  };

  const handleAskAxioPress = async () => {
    setShowChat(true);
    handleCourseworkNavigation();
  };

  const handleNavigate = async () => {
    const token = await getValidAccessToken();
    sendMessage({
      meta: {
        token: token,
        abort: true,
      },
    });

    dispatch({
      type: actions.SET_META,
      payload: {
        isMainMenuExpanded: false,
        isLearnMenuOpen: false,
        isCustomCourseworkMenuOpen: false,
      },
    });

    setNotificationsVisible(false);
    setProgressVisible(false);
  };

  const getSelectedMenuItem = (name) => {
    if (name === "School") return "Coursework";
    if (name === "Custom School Student Courses" || name === "Custom School Faculty Courses")
      return "Custom Course Student";
    return name;
  };

  return (
    <>
      {progressVisible && !coursesLoading && (
        <SlideInMenuContainer
          ref={slideInRef}
          courses={courses?.getEnrolledCoursesForStudent.data?.courses.map((course) => course.course) || []}
          visible={progressVisible}
          slideAnim={slideAnim}
          onClose={() => setProgressVisible(false)}
          onSettingsPress={handleSettingsPress}
        />
      )}

      <TopMenu
        menuItems={menuItems}
        selectedItem={getSelectedMenuItem(route.name)}
        logo={logo}
        miniLogo={miniLogo}
        showMiniLogo={showMiniLogo}
        userInitial={userInfo.userInitial}
        isLTI={isLTI}
        onItemPress={handleItemPress}
        onSettingsPress={handleProgressToggle}
        onLogoutPress={handleLogoutPress}
        onNotifcationsPress={handleNotificationsToggle}
        onAskAxioPress={handleAskAxioPress}
        companionName={companionName}
      />
    </>
  );
}

export default TopMenuContainer;
